import { FC, useState } from "react"

import { useTranslation } from "react-i18next"

import { CMS_LOCALE_NAMES, CMS_LOCALES, CMS_TEMPLATES, CmsPage, CmsPageListItem } from "services/cms-seo/type"

import { Field, FieldRow, ghostButtonClass, inputClass, SectionHeading, ToggleRow } from "../CmsUi"

interface AdvancedPaneProps {
  page: CmsPage
  onChange: (patch: Partial<CmsPage>) => void
  /** Candidate parents — pages in the same locale, excluding this one. */
  parentCandidates: CmsPageListItem[]
  authors: string[]
  onDuplicate: (toLocale: string) => Promise<void>
}

export const AdvancedPane: FC<AdvancedPaneProps> = ({ page, onChange, parentCandidates, authors, onDuplicate }) => {
  const { t } = useTranslation("translation", { useSuspense: false })

  const missingLocales = CMS_LOCALES.filter((locale) => locale !== page.locale && !page.translations[locale])
  const [duplicateLocale, setDuplicateLocale] = useState<string>(missingLocales[0] ?? "")
  const [duplicating, setDuplicating] = useState(false)

  const runDuplicate = async () => {
    if (!duplicateLocale) return
    setDuplicating(true)
    await onDuplicate(duplicateLocale)
    setDuplicating(false)
  }

  return (
    <div>
      <FieldRow>
        <Field label={t("cmsSeo.field.template", { defaultValue: "Template" })}>
          <select className={inputClass} value={page.template} onChange={(event) => onChange({ template: event.target.value })}>
            {CMS_TEMPLATES.map((template) => (
              <option key={template} value={template}>
                {template}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label={t("cmsSeo.field.parent", { defaultValue: "Parent page" })}
          hint={t("cmsSeo.field.parentHint", { defaultValue: "The URL becomes the ancestors' slugs followed by this page's slug" })}
        >
          <select
            className={inputClass}
            value={page.parent_id ?? ""}
            onChange={(event) => onChange({ parent_id: event.target.value || null })}
          >
            <option value="">{t("cmsSeo.noParent", { defaultValue: "— none —" })}</option>
            {parentCandidates.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.title} ({candidate.path})
              </option>
            ))}
          </select>
        </Field>
      </FieldRow>

      <FieldRow>
        <Field label={t("cmsSeo.field.sortOrder", { defaultValue: "Sort order" })}>
          <input
            type="number"
            className={inputClass}
            value={page.sort_order}
            onChange={(event) => onChange({ sort_order: Number(event.target.value) })}
          />
        </Field>
        <Field label={t("cmsSeo.col.author", { defaultValue: "Author" })}>
          <select className={inputClass} value={page.author ?? ""} onChange={(event) => onChange({ author: event.target.value || null })}>
            <option value="">{t("cmsSeo.noAuthor", { defaultValue: "— none —" })}</option>
            {authors.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </Field>
      </FieldRow>

      <ToggleRow
        title={t("cmsSeo.field.excludeSearch", { defaultValue: "Exclude from site search" })}
        description={t("cmsSeo.field.excludeSearchHint", { defaultValue: "Hides the page from the on-site search index only" })}
        checked={page.exclude_from_search}
        onChange={() => onChange({ exclude_from_search: !page.exclude_from_search })}
      />
      <ToggleRow
        title={t("cmsSeo.field.passwordProtect", { defaultValue: "Password protect" })}
        description={t("cmsSeo.field.passwordProtectHint", {
          defaultValue: "Useful for pages shared with a client before launch. A protected page is always noindex, nofollow."
        })}
        checked={page.password_protect}
        onChange={() => onChange({ password_protect: !page.password_protect })}
      />

      <SectionHeading>{t("cmsSeo.copyPage", { defaultValue: "Copy this page" })}</SectionHeading>

      <FieldRow>
        <Field
          label={t("cmsSeo.toLocale", { defaultValue: "To locale" })}
          hint={t("cmsSeo.copyHint", { defaultValue: "The copy starts as a draft with the same body, ready to translate." })}
        >
          <select className={inputClass} value={duplicateLocale} onChange={(event) => setDuplicateLocale(event.target.value)}>
            {missingLocales.length === 0 ? (
              <option value="">{t("cmsSeo.allLocalesExist", { defaultValue: "Every locale already has this page" })}</option>
            ) : (
              missingLocales.map((locale) => (
                <option key={locale} value={locale}>
                  {CMS_LOCALE_NAMES[locale]} ({locale.toUpperCase()})
                </option>
              ))
            )}
          </select>
        </Field>
        <div className="flex-none self-end pb-1">
          <button type="button" className={ghostButtonClass} disabled={!duplicateLocale || duplicating} onClick={runDuplicate}>
            {t("cmsSeo.duplicate", { defaultValue: "Duplicate" })}
          </button>
        </div>
      </FieldRow>
    </div>
  )
}
